/**
 * THE ONLY VIRAL METRIC THAT MATTERS.
 *
 * From the memo (§4): the shared link lands on a page showing the card and
 * "start your own table — no installs, no accounts", and `guest_became_host` is
 * its instrument. Not shares sent. Not link clicks. Not installs. The one number
 * that tells you the loop is closed is:
 *
 *   someone arrived because a friend shared a game — and went on to start a
 *   table of their own.
 *
 * Everything else is a vanity metric that can go up while the product dies.
 *
 * The mechanic is a two-part latch:
 *   1. arriving on a shared surface writes a referral stamp to localStorage;
 *   2. the first time that person CREATES a game, we fire the event and burn
 *      the stamp.
 *
 * Deliberately NOT sessionStorage: the gap between "watched the family laugh at
 * Dad's answer on Friday" and "started my own table on Sunday" is the entire
 * behaviour we are trying to measure, and a session cookie would score it zero.
 */

import { auth } from './firebase';

const KEY = 'wr.referral';
const FIRED = 'wr.referral.converted';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days — a family's convening cycle

const API_BASE = (
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '')
).replace(/\/$/, '');

export type ReferralSource =
  | 'shared-game'
  | 'invite-link'
  | 'daily'
  | 'profile'
  /**
   * They sat down at somebody ELSE'S table. Not via a link we can attribute —
   * they typed the room code, or a friend handed them the phone. This is the
   * plainest form of the invitation the product actually runs on, and without it
   * `guest_became_host` could only ever count people who arrived through a URL,
   * which is a minority of a game whose whole premise is a room full of people.
   */
  | 'joined-room';

interface Referral {
  source: ReferralSource;
  ref: string | null; // the gameId / roomCode they came in on
  at: number;
}

function read(): Referral | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const r = JSON.parse(raw) as Referral;
    if (!r?.at || Date.now() - r.at > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return r;
  } catch {
    return null;
  }
}

/**
 * "I got here from a link someone sent me."
 *
 * First write wins. If you arrive on a shared game and then poke around three
 * more shared links before starting your own table, the thing that actually
 * recruited you was the first one — overwriting it would credit the last click
 * and quietly turn this into a last-touch attribution model, which for a
 * word-of-mouth product is just noise.
 */
export function rememberReferral(source: ReferralSource, ref: string | null = null): void {
  try {
    if (localStorage.getItem(FIRED)) return; // already converted; stop tracking
    if (read()) return;
    localStorage.setItem(KEY, JSON.stringify({ source, ref, at: Date.now() } satisfies Referral));
  } catch {
    /* private mode — losing an analytics stamp must never break the page */
  }
}

/** Are we currently a guest who arrived via somebody's share? */
export function pendingReferral(): Referral | null {
  return read();
}

async function post(eventType: string, eventData: Record<string, unknown>): Promise<void> {
  const user = auth?.currentUser;
  if (!user || !API_BASE) return;
  try {
    const token = await user.getIdToken();
    await fetch(`${API_BASE}/analytics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ eventType, userId: user.uid, eventData }),
    });
  } catch {
    /* analytics is never allowed to be load-bearing */
  }
}

/**
 * Call when a user CREATES a game. Fires `guest_became_host` exactly once, and
 * only for someone who genuinely arrived through a share.
 */
export async function markBecameHost(roomCode?: string): Promise<boolean> {
  const referral = read();
  if (!referral) return false;

  try {
    if (localStorage.getItem(FIRED)) return false;
    // Burn the latch BEFORE the network call: a retry, a double-tap on "Play
    // Now", or a React StrictMode double-invoke must not each count as a
    // separate conversion. Better to lose an event than to inflate the one
    // number we have decided to steer by.
    localStorage.setItem(FIRED, String(Date.now()));
    localStorage.removeItem(KEY);
  } catch {
    return false;
  }

  await post('guest_became_host', {
    source: referral.source,
    ref: referral.ref,
    // How long the loop took to close, in hours. The distribution of this is
    // the difference between "they clicked through immediately" and "they
    // remembered us on Sunday", and those are different products.
    hoursToConvert: Math.round(((Date.now() - referral.at) / 3_600_000) * 10) / 10,
  });
  return true;
}

/** A shared surface was opened. Cheap, and it gives the funnel a denominator. */
export function trackShareLanding(source: ReferralSource, ref: string | null = null): void {
  rememberReferral(source, ref);
  void post('share_link_opened', { source, ref });
}

/**
 * THE SECOND CONVERSION: a guest became an account.
 *
 * `guest_became_host` says the loop closed. This says the person stayed. It is
 * fired from firebase.ts at the moment of the credential link, because that is
 * the only place that knows the fact that actually matters — whether the uid
 * SURVIVED.
 *
 * `uidPreserved: true` means linkWithPopup attached Google to the existing
 * anonymous account and every gameRecord, friendship and stat keyed to that uid
 * still resolves. `false` means the Google account already existed, Firebase
 * refused to merge, and the guest's uid was abandoned along with its history —
 * a real, countable loss, and one we would rather see in the data than pretend
 * away. Recording only the successes would make the number a lie by omission.
 *
 * Not latched behind a referral like markBecameHost: signing up matters whether
 * or not somebody's link brought you here. If a referral stamp IS present, we
 * attach it, so the two events join on the same journey.
 */
export async function markGuestSignedIn(args: {
  guestUid: string;
  finalUid: string;
  uidPreserved: boolean;
}): Promise<void> {
  const referral = read();
  await post('guest_signed_in', {
    guestUid: args.guestUid,
    finalUid: args.finalUid,
    uidPreserved: args.uidPreserved,
    // Was this person recruited by a share? Null means they found us some other
    // way — which is itself worth knowing, and is not the same as zero.
    referralSource: referral?.source ?? null,
    referralRef: referral?.ref ?? null,
    method: 'google',
  });
}

/** A card actually left the building. */
export function trackShareSent(outcome: string, gameId: string | null): void {
  void post('share_sent', { outcome, gameId });
}
